import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import ErrorMessage from '@components/Common/ErrorMessage';
import type { Column } from '@components/Common/SearchFilter/SearchFilter';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { jobSeeker } from '@constants/graphQLQueries';
import { LANGUAGES } from '@constants/languages';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { buildFilterArray, graphQLFilterArrayFormatter } from '@helpers/utils';
import PeopleIcon from '@mui/icons-material/People';
import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate, formatDateTime, formatRelative } from '@helpers/date';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const JOBSEEKER_COLUMNS = ['name', 'email', 'location', 'lastLogin', 'createdAt'];

const rowsPerPage = 100;

interface JobSeekerData {
  id: string;
  name: string;
  email: string;
  phone: string;
  locality: string;
  city: string;
  state: string;
  country: string;
  address: string;
  preferedLocation: string;
  resumeUrl: any;
  lastLoginAt: string | null;
  createdAt?: string | null;
}

// Last login is relative for a week and a date after that; see formatRelative.
const formatLastLogin = (value: string | null | undefined): string => formatRelative(value);


const LOCATION_PREF_FIELD: Record<string, string> = {
  State: 'jobPreferences.preferredStates',
  Country: 'jobPreferences.preferredCountries',
};

const JobSeekers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<JobSeekerData[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [filterData, setFilterData] = useState<Column[]>(() => {
    const initial: Column[] = [];
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter_') && value.trim() !== '') {
        const col = key.replace('filter_', '');
        initial.push({ id: initial.length + 1, columnName: col, searchValue: decodeURIComponent(value) });
      }
    });
    return initial;
  });
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('date_from') ?? '');
  const [dateTo, setDateTo] = useState(() => searchParams.get('date_to') ?? '');

  const navigate = useNavigate();

  const renderLocation = (row: any) => {
    const { locality, city, state, country, address, preferedLocation } = row;
    const parts = [locality, city, state, country].filter(Boolean);
    if (parts.length > 0) return parts.join(', ');
    if (address) return address;
    if (preferedLocation) return preferedLocation;
    return '-';
  };

  const userColMapping = {
    Name: 'personalDetails.fullName',
    Email: 'personalDetails.email',
    'Phone Number': 'personalDetails.phone',
    Locality: 'personalDetails.locality',
    /*
     * A region, not a field: anyone who LIVES in the city or one of its areas (Settings > Mumbai
     * areas), or PREFERS it. It read personalDetails.city alone, which is why Mumbai showed about
     * 400 -- people write Andheri, Thane or Bombay, and a CV often fills only currentLocation.
     */
    City: 'cityRegion',
    State: 'personalDetails.state',
    Country: 'personalDetails.country',
    Skills: 'skills',
    /*
     * Maps straight onto the field. No backend change was needed: FilterCriteriaUtils turns any
     * key into a case-insensitive regex and Mongo applies a regex to any element of a string
     * array, which is how Skills above already works.
     *
     * Deliberately NOT first in this object: `columns[0]` becomes the quick-search box in
     * SearchFilter, and that has to stay Name.
     */
    Languages: 'languages',
    'Preferred City': 'jobPreferences.preferredCities',
    Source: 'isExternal',
    /*
     * Filters on the structured profile (work history, education, certificates, experience,
     * pay and notice bounds) are held back until enough profiles carry the data: on a list where
     * most candidates have none of it, they filter almost everyone out and read as broken. The
     * backend already supports them (FilterCriteriaUtils __gte/__lte, currentPayAnnual); adding
     * the entries back here is all it takes.
     */
  };

  const filterColumns = Object.keys(userColMapping);
  const skipResetOnFirstFilterChange = useRef(true);



  const onApplyFilters = useCallback(() => {
    if (skipResetOnFirstFilterChange.current) {
      skipResetOnFirstFilterChange.current = false;
      return;
    }
    if (page !== 0) setPage(0);
  }, [page]);

  const handleChangePage = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
      const newSearch = new URLSearchParams(searchParams);
      newSearch.set('page', String(newPage));
      if (newSearch.toString() !== searchParams.toString()) setSearchParams(newSearch);
    },
    [searchParams, setSearchParams],
  );

  const fetchData = useCallback(
    async (page: number) => {
      setLoading(true);
      setError(null);
      const filterArr = buildFilterArray(filterData, userColMapping, LOCATION_PREF_FIELD);
      // 'Source' is a friendly label over the boolean isExternal field.
      filterArr.forEach((f) => {
        if (f.key === 'isExternal') {
          f.value = /external/i.test(f.value) ? 'true' : 'false';
        }
      });
      if (dateFrom) filterArr.push({ key: 'createdAtFrom', value: dateFrom, operator: 'AND' });
      if (dateTo) filterArr.push({ key: 'createdAtTo', value: dateTo, operator: 'AND' });
      const filter = graphQLFilterArrayFormatter(filterArr);
      try {
        const query = jobSeeker(page, rowsPerPage, filter);
        const { data } = await fetchGraphQLApiData(query);
        const { getAllJobSeekers } = data;
        if (Array.isArray(getAllJobSeekers.content)) {
          const jobSeekers: JobSeekerData[] = getAllJobSeekers.content.filter((item: any) => item?.personalDetails != null).map((item: any) => ({
            id: item._id,
            name: item.personalDetails.fullName,
            email: item.personalDetails.email,
            phone: item.personalDetails.phone || '',
            locality: item.personalDetails.locality ?? '',
            city: item.personalDetails.city,
            state: item.personalDetails.state,
            country: item.personalDetails.country,
            address: item.personalDetails.address,
            preferedLocation:
              Array.isArray(item.jobPreferences?.preferredLocalities) &&
              item.jobPreferences.preferredLocalities.length > 0
                ? item.jobPreferences.preferredLocalities.join(', ')
                : Array.isArray(item.jobPreferences?.preferredCities) &&
                  item.jobPreferences.preferredCities.length > 0
                ? item.jobPreferences.preferredCities.join(', ')
                : '-',
            resumeUrl: '',
            lastLoginAt: item.lastLoginAt ?? null,
            createdAt: item.createdAt ?? null,
          }));
          setData(jobSeekers);
          setTotalElements(getAllJobSeekers.totalElements ?? 0);
        } else {
          throw new Error('Unexpected response format');
        }
      } catch (error) {
        console.error('Error fetching job seekers:', error);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setLoading(false);
      }
    },
    [filterData, dateFrom, dateTo],
  );

  useEffect(() => { fetchData(page); }, [fetchData, page, filterData]);

  useEffect(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    const safe = Number.isFinite(p) && p >= 0 ? p : 0;
    if (safe !== page) setPage(safe);
    setDateFrom(searchParams.get('date_from') ?? '');
    setDateTo(searchParams.get('date_to') ?? '');
  }, [searchParams.toString()]);

  const skipDateUrlSyncOnMount = useRef(true);
  useEffect(() => {
    if (skipDateUrlSyncOnMount.current) {
      skipDateUrlSyncOnMount.current = false;
      return;
    }
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      if (dateFrom) n.set('date_from', dateFrom); else n.delete('date_from');
      if (dateTo) n.set('date_to', dateTo); else n.delete('date_to');
      return n;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  // A row opens the entity workspace at its own route. This used to swap the
  // list out for a popup rendered in place, which is why the URL and the
  // screen could disagree.
  const handleOpenRecord = (userId: string) => navigate(`/jobseekers/${userId}`);

  return (
    <CRMPageTemplate
      addNewLabel="Import Resume"
      filterColumns={filterColumns}
      columnOptions={{ Source: ['External', 'Platform'], Languages: LANGUAGES }}
      locationColumns={['City', 'State', 'Country', 'Preferred City']}
      locationColumnTypeMapping={{ 'Preferred City': 'city' }}
      localityColumns={['Locality']}
      localityAutoFillColumns={{ Locality: { city: 'City', state: 'State', country: 'Country' } }}
      filterLabel="Filter"
      icon={<PeopleIcon />}
      initialColumns={filterData}
      setFilterData={setFilterData}
      title="Job Seekers"
      totalCount={totalElements}
      onAddNew={() => navigate('/jobseekers/import')}
      onApplyFilters={onApplyFilters}
      dateFrom={dateFrom}
      dateTo={dateTo}
      onDateFromChange={v => setDateFrom(v)}
      onDateToChange={v => setDateTo(v)}
    >

      {loading ? (
        <SkeletonLoader rows={20} columns={JOBSEEKER_COLUMNS.length} />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : data.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
          <Typography color="text.secondary" variant="body2">No job seekers found.</Typography>
        </Box>
      ) : (
        <MintlyTable
          pagination={{
            enabled: true,
            page,
            rowsPerPage,
            totalNumberOfRows: totalElements,
            handleChangePage,
          }}
          rows={data}
          selectors={JOBSEEKER_COLUMNS}
          tableBodyRow={{
            name: (row: any) => <>{row.name || '-'}</>,
            email: (row: any) => <>{row.email || '-'}</>,
            location: (row: any) => <>{renderLocation(row)}</>,
            lastLogin: (row: any) => (
              <span title={row.lastLoginAt ? formatDateTime(row.lastLoginAt) : 'Never logged in'}
                style={{ color: !row.lastLoginAt ? 'var(--color-text-muted)' : 'inherit' }}>
                {formatLastLogin(row.lastLoginAt)}
              </span>
            ),
            createdAt: (row: any) => <>{formatDate(row.createdAt)}</>,
          }}
          tableHeadRow={{
            name: <>Name</>,
            email: <>Email</>,
            location: <>Location</>,
            lastLogin: <>Last Login</>,
            createdAt: <>Created</>,
          }}
          onRowClick={(row) => handleOpenRecord(row.id)}
        />
      )}
    </CRMPageTemplate>
  );
};

export default JobSeekers;
